// React imports
import React from 'react';
import { useRouteError } from 'react-router-dom';

// Generic imports
import queryString from 'query-string';

const ErrorPage = (props) => {
    // Extract the organization from the query string
    const { org, reason, stack, me, path: errorPath } = queryString.parse(window.location.search);
    const error = useRouteError();

    if (!org && !reason && !stack && !me) {
        const org = window.location.pathname.split('/')[2];
        const path = window.location.href;

        // Handle cases where error might not be an Error object
        let errorMessage = '';
        let errorStack = '';

        if (error instanceof Error) {
            errorMessage = error.message || '';
            errorStack = error.stack || '';
        } else if (error && typeof error === 'object') {
            errorMessage = error.message || error.toString() || JSON.stringify(error);
            errorStack = error.stack || '';
        } else if (error !== null && error !== undefined) {
            errorMessage = String(error);
            errorStack = '';
        }

        // Safely extract email from window.me
        const userEmail = (window.me && window.me.email) ? window.me.email : '';

        return (
            <iframe
                src={`/error?path=${encodeURIComponent(path)}&org=${encodeURIComponent(org)}&reason=${encodeURIComponent(errorMessage)}&stack=${encodeURIComponent(errorStack)}&me=${encodeURIComponent(userEmail)}`}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    border: 'none',
                    zIndex: 9999999,
                    width: '100%',
                    height: '100%',
                }}
            />
        );
    }

    console.error('Error:', reason, stack, me, error);

    const orgAlias = org || window.location.pathname.split('/')[2] || '';
    const homeUrl = orgAlias ? `/organization/${orgAlias}` : '/';

    return (
        <div style={{
            height: '100vh',
            background: '#f5f7fa',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
            WebkitFontSmoothing: 'antialiased',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
        }}>
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
            <style>{`
                @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
                @keyframes spin { 100% { transform: rotate(360deg); } }
            `}</style>

            <main style={{
                textAlign: 'center',
                width: '100%',
                maxWidth: '420px',
                padding: '40px 36px',
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
                border: '1px solid #edf2f7',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Top accent */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #5e5ce6, #8e8df3)' }} />

                {/* Logo */}
                <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'center' }}>
                    <img src="/monada-logo.png" alt="Monada" style={{ height: '30px', width: 'auto' }} />
                </div>

                {/* Icon */}
                <div style={{
                    width: '72px', height: '72px', margin: '0 auto 24px', background: '#f0f0ff',
                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'float 3s ease-in-out infinite',
                }}>
                    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#5e5ce6" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>

                {/* Copy */}
                <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#16203a', marginBottom: '8px', lineHeight: 1.3 }}>
                    Something didn't load correctly
                </h1>
                <p style={{ fontSize: '15px', color: '#5d6a82', lineHeight: 1.5, marginBottom: '28px' }}>
                    We ran into an unexpected issue. Click <b style={{ color: '#16203a', fontWeight: 600 }}>Refresh</b> to return to where you were.
                </p>

                {/* Refresh button */}
                <button
                    onClick={() => { window.parent.location.href = window.location.origin + homeUrl; }}
                    style={{
                        width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                        background: '#5e5ce6', color: '#ffffff', border: 'none', borderRadius: '12px',
                        padding: '14px 28px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                        fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(94, 92, 230, 0.25)',
                    }}
                >
                    <i className="fa-solid fa-arrows-rotate" style={{ fontSize: '16px' }}></i>
                    Refresh Page
                </button>

                {/* Diagnostics */}
                {reason && (
                    <>
                        <div style={{ borderTop: '1px solid #edf2f7', margin: '24px 0' }} />
                        <details style={{ textAlign: 'left' }}>
                            <summary style={{
                                color: '#98a2b3', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                                letterSpacing: '0.05em', cursor: 'pointer', padding: '4px 0', listStyle: 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                Technical Details
                                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </summary>
                            <div style={{
                                marginTop: '12px', padding: '12px', background: '#f7fafc', border: '1px solid #edf2f7',
                                borderRadius: '8px', fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                                fontSize: '11px', color: '#5d6a82', lineHeight: 1.6,
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#98a2b3' }}>Error:</span>
                                    <span style={{ fontWeight: 600, color: '#e53e3e', maxWidth: '250px', textAlign: 'right', wordBreak: 'break-word' }}>{reason}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                    <span style={{ color: '#98a2b3' }}>Timestamp:</span>
                                    <span style={{ fontWeight: 600 }}>{new Date().toISOString().replace('T', ' ').substring(0, 19)} UTC</span>
                                </div>
                                {me && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <span style={{ color: '#98a2b3' }}>User:</span>
                                        <span style={{ fontWeight: 600 }}>{me}</span>
                                    </div>
                                )}
                            </div>
                        </details>
                    </>
                )}
            </main>

            {/* Footer */}
            <footer style={{ marginTop: '24px', fontSize: '12px', color: '#98a2b3', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
                <a href="mailto:help@monada.ai" style={{ color: '#98a2b3', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Contact Support
                </a>
            </footer>
        </div>
    );
};

export default ErrorPage;
